import type {
  ScriptureUtilMissingTranslationError,
  Verse,
} from "@rsc/scripture-util";
import { createVerse, VidRange } from "@rsc/scripture-util";
import type { StandaloneVerseData } from "@rsc/scripture-model";
import type {
  ScriptureClientError,
  ScriptureClientInterface,
} from "@rsc/scripture-client";
import { ResultAsync } from "neverthrow";
import { distinct } from "~/units/arrays";
import type { Dict } from "~/types";

/**
 * An item in the Bible grid.
 */
interface BibleGridItem {
  /**
   * A unique key in the grid.
   */
  key: string;

  /**
   * Info about the verse in this grid position.
   */
  verse?: Verse;

  /**
   * Extra markup for this item.
   */
  markup?: string;

  /**
   * True if this item is the top-most item in its column.
   */
  firstVerseInColumn: boolean;

  /**
   * Extra CSS classes for this item.
   */
  classes: Dict<boolean>;

  /**
   * Extra CSS styles for this item.
   */
  styles: Dict<string>;

  /**
   * The CSS grid position for this item.
   */
  grid: {
    row: {
      start: number;
      end: number;
    };
    column: {
      start: number;
      end: number;
    };
  };
}

/**
 * Load the grid items containing Bible verses.
 *
 * @param client
 *   The scripture client used to get info from the scripture server.
 * @param tids
 *   The IDs of the translations for which to load verses.
 * @param fromVid
 *   The ID of the first verse to load.
 * @param toVid
 *   The ID of the last verse to load.
 *
 * @returns
 *   A flat array of Bible grid items.
 */
export function getVerseGridItems(
  client: ScriptureClientInterface,
  tids: string[],
  fromVid: number,
  toVid: number,
): ResultAsync<
  BibleGridItem[],
  ScriptureClientError | ScriptureUtilMissingTranslationError
> {
  // Get all verses that intersect with the current range, in all open translations, in column-major order.
  const allVersesResult = ResultAsync.combine(
    tids.map((tid) => {
      return new VidRange(fromVid, toVid, tid).getAllVerses(client);
    }),
  );

  return allVersesResult.map((allVerses) => {
    const allVids = allVerses
      .flat()
      .map((data) => data.fromVid)
      .sort((a, b) => a - b)
      .filter(distinct);

    // Process the grid items into a flat array of grid items.
    return processGridItems(allVids, allVerses);
  });
}

/**
 * Process the grid items that make up the grid content area.
 *
 * @param allVids
 *   All possible verse IDs in the grid, without repetition, in ascending order.
 * @param allVerses
 *   The verses to show, in column-major order.
 *   The outer loop is the columns/translations.
 *   The inner loop is the rows/verses.
 *
 * @returns
 *   A flat array of Bible grid items.
 */
export function processGridItems(
  allVids: number[],
  allVerses: StandaloneVerseData[][],
): BibleGridItem[] {
  const items: BibleGridItem[] = [];
  let lastVerse: StandaloneVerseData | null = null;

  allVerses.forEach((versesInColumn, columnIndex) => {
    let odd = false;
    let first = true;

    // Get a mapping of vid to array index, because the translation may skip verses.
    const vidToIndex = versesInColumn.reduce((obj: Dict<number>, v, i) => {
      obj[v.fromVid] = i;
      return obj;
    }, {});

    allVids.forEach((vid, rowIndex) => {
      odd = !odd;

      const verseData: StandaloneVerseData | undefined =
        versesInColumn[vidToIndex[vid] ?? -1];

      if (verseData) {
        items.push({
          key: `${verseData.tid}-${verseData.fromVid}`,
          classes: {
            "verse-line": true,
            odd,
            even: !odd,
          },
          styles: {},
          grid: {
            row: {
              start: rowIndex + 1,
              end: rowIndex + 2,
            },
            column: {
              start: columnIndex + 1,
              end: columnIndex + 2,
            },
          },
          verse: createVerse(verseData),
          firstVerseInColumn: first,
        });
        first = false;
        lastVerse = verseData;
      } else {
        // This column / translation does not have this verse ID, but at least one other open translation does.
        if (lastVerse && lastVerse.fromVid <= vid && lastVerse.toVid >= vid) {
          // The previous verse is a compound verse, and should also cover this cell.
          const previousItem = items[items.length - 1];
          if (previousItem) {
            previousItem.grid.row.end = rowIndex + 2;
          }
        }
        // else this grid position will simply be empty.
      }
    });
  });

  return items;
}
